import { loadStripe } from '@stripe/stripe-js';
import _ from 'lodash';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePublicAPI } from '../../apis/public-api';
import { PaymentWalletModalBody } from '../../components/payment/payment-wallet-modal-body';
import { PaymentWalletModalTitle } from '../../components/payment/payment-wallet-modal-title';
import { useErrorHandler } from '../../hooks/use-error-handler';
import { useFeature } from '../../hooks/use-feature';
import { usePaymentInterval } from '../../hooks/use-payment-interval';
import { useShake } from '../../hooks/use-shake';
import { useStorage } from '../../hooks/use-storage';
import { useSwal } from '../../hooks/use-swal';
import { layoutDefaults } from '../../utils/layout-defaults';
import { PaymentType } from '../../utils/manager/card-manager';
import { usePaymentCardManager } from '../../utils/manager/payment-card-manager';
import { useAnalytics } from '../../utils/setup/analytics-setup';

function usePaymentWalletModal() {
  const storage = useStorage();
  const { spot, setPaying, menuLayout, config, paymentRef, menuInfo } = storage;
  const { generateAndStorePaymentCard, cancelPaymentCard, refreshPaymentCard } =
    usePaymentCardManager();
  const { getPayWalletPrivacyPolicyFromRemote } = usePublicAPI();
  const { handleErrorOnModal } = useErrorHandler();
  const analytics = useAnalytics();
  const { analyticsPageView } = analytics;
  const { t } = useTranslation();

  const feature = useFeature();
  const { swal } = useSwal({ storage });

  const [loading, setLoading] = useState(false);
  const [focused, setFocused] = useState(false);
  const walletRef = useRef();
  const confirmButtonTextRef = useRef();

  const ordersRef = useRef();
  const tipAmountRef = useRef();
  const stripePromiseRef = useRef();
  const wasCancelledRef = useRef(false);
  const wasPaidRef = useRef(false);
  const [ready, setReady] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const onPaymentPaid = async () => {
    wasPaidRef.current = true;
    swal.close();
  };

  const onPaymentCancelled = async () => {
    wasCancelledRef.current = true;
    swal.close();
  };

  usePaymentInterval(
    {
      payment: paymentRef.current,
      orders: ordersRef.current,
      onPaymentPaid,
      onPaymentCancelled,
    },
    focused && !loading,
    [focused, loading]
  );

  useShake(
    {
      element: confirmButtonTextRef?.current?.parentElement,
      enabled: ready && !cancelling,
    },
    [ready, cancelling]
  );

  const didOpen = () => {
    analyticsPageView(`Payment ${walletRef.current} Detail`);
    setPaying(true);
    setFocused(true);

    swal.getConfirmButton().disabled = true;
  };

  const didDestroy = () => {
    setPaying(false);
    setFocused(false);
  };

  async function prepare({ wallet, orders, tipAmount }) {
    try {
      walletRef.current = wallet;

      ordersRef.current = orders;
      tipAmountRef.current = tipAmount;
      stripePromiseRef.current = await loadStripe(config?.stripe?.api_key);

      if (
        paymentRef.current == null ||
        (paymentRef.current === 'paying' && _.isEmpty(paymentRef.current.card))
      ) {
        await generateAndStorePaymentCard({
          orders,
          tipAmount,
          type: PaymentType.WALLET,
          wallet,
        });
      }
    } catch (error) {
      handleErrorOnModal(error);
    }

    return null;
  }

  async function show({
    autoLoaded,
    prepareOnPreConfirm,
    prepareOnPreDeny,
  } = {}) {
    setCancelling(false);
    let wasSucccess = false;

    const result = await swal.fire({
      title: (
        <PaymentWalletModalTitle
          style={{
            marginTop: layoutDefaults.biggerVerticalMargin,
          }}
          wallet={walletRef.current}
          title={t('wallet-payment.title')}
          analytics={analytics}
          getPrivacyPolicyFromRemote={getPayWalletPrivacyPolicyFromRemote}
        />
      ),
      html: (
        <PaymentWalletModalBody
          spot={spot}
          wallet={walletRef.current}
          stripePromise={stripePromiseRef.current}
          menuInfo={menuInfo}
          menuLayout={menuLayout}
          cancelling={cancelling}
          clientSecret={paymentRef.current?.card.client_secret}
          intentId={paymentRef.current?.card.intent_id}
          payment={paymentRef.current}
          feature={feature}
          onReady={() => {
            setReady(true);
            swal.enableButtons();
          }}
          onError={(error) => {
            handleErrorOnModal(error);
            swal.hideLoading();
          }}
          onClick={(event) => {
            swal.resetValidationMessage();
            swal.showLoading();
            event.resolve();
            setLoading(true);
          }}
          onConfirm={async () => {
            await preConfirm({ prepareOnPreConfirm });
            wasSucccess = true;
            swal.close();
          }}
          onCancel={() => {
            swal.showValidationMessage(t('payment-interrupted'));
            swal.hideLoading();
            setLoading(false);
          }}
        />
      ),
      showLoaderOnConfirm: true,
      showLoaderOnDeny: true,
      showConfirmButton: true,
      showDenyButton: true,
      preConfirm: () => {
        swal.showValidationMessage(t('payment-method-not-supported'));
        return false;
      },
      preDeny: () => preDeny({ prepareOnPreDeny }),
      confirmButtonText: (
        <span ref={confirmButtonTextRef}>{t('payment-card-modal.pay')}</span>
      ),
      denyButtonText: autoLoaded ? t('cancel') : t('back'),
      didOpen,
      didDestroy,
    });

    if (wasSucccess) {
      result.isConfirmed = true;
      result.isDismissed = false;
      // setPayment(null);
    }

    return result;
  }

  const preConfirm = async ({ prepareOnPreConfirm }) => {
    setLoading(true);

    try {
      await refreshPaymentCard({
        payment: paymentRef.current,
        orders: ordersRef.current,
      });

      await prepareOnPreConfirm?.call(this);
      // }
    } catch (error) {
      handleErrorOnModal(error);
      swal.hideLoading();
      setLoading(false);
      return false;
    }

    setLoading(false);
    return true;
  };

  async function preDeny({ prepareOnPreDeny }) {
    setLoading(true);

    try {
      setCancelling(true);
      await cancelPaymentCard(paymentRef.current);
      await prepareOnPreDeny?.call(this);
    } catch (error) {
      handleErrorOnModal(error);
      setCancelling(false);
      setLoading(false);
      return false;
    }

    setLoading(false);
    return true;
  }

  return {
    preparePaymentWalletModal: prepare,
    showPaymentWalletModal: show,
  };
}

export { usePaymentWalletModal };
